import { App, Button, Form, Input, Popconfirm, Select, Tooltip } from "antd"
import dayjs from "dayjs"
import { maxBy } from "lodash"
import { useMemo, useState } from "react"
import styled from "styled-components"

import { versionAtoms } from "#atoms/allAtoms"
import Flex from "#blocks/Flex"
import Modal from "#blocks/Modal"
import {
  type FirebaseEnv,
  getFirebaseEnv,
  setFirebaseEnv,
} from "#classes/Firebase"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import { useUserContext } from "#contexts/UserContextProvider"
import { USER_ROLES } from "#customTypes/composedTypes/authTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import {
  NOTIFICATION_SUBTYPE,
  NOTIFICATION_TYPE,
} from "#customTypes/composedTypes/notificationTypes"
import { UserConstructor } from "#customTypes/composedTypes/userTypes"
import { VersionConstructor } from "#customTypes/composedTypes/versionTypes"
import { EnumValues } from "#customTypes/types"
import { useCreateNotification } from "#hooks/jotai/useCreateNotification"
import { useSaveDoc } from "#hooks/jotai/useSaveDoc"
import { useAllDocs } from "#hooks/jotai/useSubscription"
import { useGetAllUsers } from "#hooks/newHooks/useGetUser"
import { STANDARD_DATE_TIME_FORMAT } from "#utils/dateHelpers"
import { canAssignRole } from "#utils/permissionsHelpers"
import { camelToWords } from "#utils/textFormatters"

const SSection = styled.div<{ $bgColor: string }>`
  background-color: ${({ $bgColor }) => $bgColor};
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
`

const SLabel = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
  color: white;
`

interface AdminControlsModalProps {
  open: boolean
  onClose: () => void
}

function parseChangelogItems(raw: string): string[] {
  return raw
    .replace(/\r\n/g, "\n")
    .replace(/\\n/g, "\n")
    .split("\n")
    .map((line) => line.replace(/^[\s]*[•\-\*]\s*/, "").trim())
    .filter(Boolean)
}

const AdminControlsModal = ({ open, onClose }: AdminControlsModalProps) => {
  const theme = useThemeContext()
  const { message } = App.useApp()
  const {
    currentUser,
    canImpersonate,
    startImpersonation,
    startRoleOverride,
    currentUserId,
  } = useUserContext()
  const allUsers = useGetAllUsers()
  const versionDocs = useAllDocs(versionAtoms, DOC_TYPES.VERSIONS)
  const latestVersionDoc = useMemo(
    () => maxBy(versionDocs, (doc) => doc.version),
    [versionDocs]
  )
  const { saveDoc } = useSaveDoc<VersionConstructor>(DOC_TYPES.VERSIONS)
  const { broadcastNotification } = useCreateNotification()

  const isDev = currentUser?.role === USER_ROLES.DEV
  const firebaseEnv: FirebaseEnv = getFirebaseEnv()

  const handleEnvSwitch = () => {
    setFirebaseEnv(firebaseEnv === "PROD" ? "DEV" : "PROD")
  }

  const impersonatableUsers = useMemo(() => {
    return allUsers.filter(
      (user): user is UserConstructor =>
        user.id !== currentUserId &&
        user.role !== USER_ROLES.DISABLED &&
        canImpersonate(user)
    )
  }, [allUsers, currentUserId, canImpersonate])

  const handleRoleChange = (value: EnumValues<typeof USER_ROLES>) => {
    startRoleOverride(value)
    onClose()
  }

  const handleImpersonateChange = (userId: string | null) => {
    if (userId) {
      const targetUser = impersonatableUsers.find((u) => u.id === userId)
      if (targetUser) {
        startImpersonation(targetUser)
        onClose()
      }
    }
  }

  const [changelogMessage, setChangelogMessage] = useState("")

  const sendChangelogNotification = async (
    nextVersionIso: string,
    nextChangelogItems: string[]
  ) => {
    const activeUserIds = allUsers
      .filter((u) => u.role !== USER_ROLES.DISABLED)
      .map((u) => u.id)

    await broadcastNotification(activeUserIds, {
      type: NOTIFICATION_TYPE.CHANGELOG,
      subtype: NOTIFICATION_SUBTYPE.CHANGELOG_NEW,
      header: "New features available",
      message: "Check out what's new in the latest update.",
      metadata: {
        changelogVersion: nextVersionIso,
        changelogItems: nextChangelogItems,
      },
    })
    return activeUserIds.length
  }

  const handleUpdateVersion = async () => {
    const newVersion = new Date().toISOString()
    const changelogItems = parseChangelogItems(changelogMessage)

    if (changelogItems.length > 0) {
      const notifiedUsersCount = await sendChangelogNotification(
        newVersion,
        changelogItems
      )
      message.success(
        `Changelog notification sent to ${notifiedUsersCount} users`
      )
      setChangelogMessage("")
    }

    await saveDoc({
      id: newVersion,
      version: newVersion,
      changelogItems: changelogItems.length > 0 ? changelogItems : undefined,
    })
    message.success(`Version updated to ${newVersion}`)
  }

  // Get available roles (exclude current user's real role since that's "normal" mode)
  const availableRoles = Object.values(USER_ROLES).filter((r) => {
    if (!currentUser?.role) return false
    if (r === currentUser.role) return false
    return canAssignRole(currentUser.role, r)
  })

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Admin Controls"
      width={500}
      footer={null}
      modalName="admin-controls"
    >
      <Flex col gap={8}>
        <SSection $bgColor={theme.error}>
          <SLabel>Role Override</SLabel>
          <Form.Item style={{ marginBottom: 0 }}>
            <Select
              style={{ width: "100%" }}
              placeholder="Select role to view as..."
              onChange={handleRoleChange}
            >
              {availableRoles.map((roleOption) => (
                <Select.Option key={roleOption} value={roleOption}>
                  {camelToWords(roleOption)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </SSection>

        <SSection $bgColor={theme.warning}>
          <SLabel>User Impersonation</SLabel>
          <Form.Item style={{ marginBottom: 0 }}>
            <Select
              style={{ width: "100%" }}
              placeholder="Select user to impersonate..."
              showSearch
              optionFilterProp="label"
              onChange={handleImpersonateChange}
              options={impersonatableUsers.map((user) => ({
                value: user.id,
                label: `${user.name} (${camelToWords(user.role ?? "")})`,
              }))}
              disabled={impersonatableUsers.length === 0}
              notFoundContent={
                impersonatableUsers.length === 0
                  ? "No users available to impersonate"
                  : "No matching users"
              }
            />
          </Form.Item>
        </SSection>

        {isDev && (
          <SSection $bgColor={theme.mainColor}>
            <SLabel>Update Version</SLabel>
            <Flex col gap={8}>
              <Input.TextArea
                value={changelogMessage}
                onChange={(e) => setChangelogMessage(e.target.value)}
                placeholder={
                  "Optional changelog (sent with force refresh):\n• New dashboard layout\n• Bug fixes for attendance tracking"
                }
                autoSize={{ minRows: 3, maxRows: 8 }}
                style={{ fontSize: 13 }}
              />
              <Flex style={{ alignItems: "center", gap: 12 }}>
                <Tooltip
                  title={
                    latestVersionDoc
                      ? `Last updated: ${dayjs(latestVersionDoc.version).format(STANDARD_DATE_TIME_FORMAT)}`
                      : "No version published yet"
                  }
                >
                  <span style={{ color: "white", fontSize: 12 }}>
                    Current:{" "}
                    {latestVersionDoc
                      ? dayjs(latestVersionDoc.version).format("MMM D, h:mm A")
                      : "Not set"}
                  </span>
                </Tooltip>
                <Popconfirm
                  title="Update Version"
                  description={
                    changelogMessage.trim()
                      ? "This will send the changelog notification and refresh all users' browsers. Are you sure?"
                      : "This will refresh all users' browsers. Are you sure?"
                  }
                  onConfirm={handleUpdateVersion}
                  okText="Yes"
                  cancelText="Cancel"
                  okButtonProps={{ danger: true }}
                >
                  <Button danger size="small">
                    Force Refresh All
                  </Button>
                </Popconfirm>
              </Flex>
            </Flex>
          </SSection>
        )}

        {isDev && (
          <SSection
            $bgColor={firebaseEnv === "PROD" ? theme.error : theme.success}
          >
            <SLabel>Firebase Environment</SLabel>
            <Flex style={{ alignItems: "center", gap: 12 }}>
              <span style={{ color: "white", fontSize: 12 }}>
                Currently connected to: <strong>{firebaseEnv}</strong>
              </span>
              <Popconfirm
                title={`Switch to ${firebaseEnv === "PROD" ? "DEV" : "PROD"}?`}
                description="This will reload the page and reconnect to the other Firebase project."
                onConfirm={handleEnvSwitch}
                okText="Switch"
                cancelText="Cancel"
                okButtonProps={{ danger: firebaseEnv === "DEV" }}
              >
                <Button
                  size="small"
                  danger={firebaseEnv === "DEV"}
                  type={firebaseEnv === "PROD" ? "primary" : "default"}
                >
                  Switch to {firebaseEnv === "PROD" ? "DEV" : "PROD"}
                </Button>
              </Popconfirm>
            </Flex>
          </SSection>
        )}
      </Flex>
    </Modal>
  )
}

export default AdminControlsModal
